<script setup>
// SiteUp V2 — o miolo do anúncio de origem (CTWA), em um lugar só.
//
// Nasceu dentro do AdArrivalNotice (o card no topo do fio). Quando o painel de
// contato ganhou o botão "Infos", os mesmos campos precisavam aparecer em dois
// lugares — e duas cópias do mesmo bloco divergem na primeira mudança.
//
// CT-B (2026-08-12): ganhou o criativo do anúncio, os chips de campanha e o
// botão "Dados técnicos". Tudo condicional: as 1.111 conversas antigas não têm
// criativo nem UTM e continuam vendo exatamente o que viam antes.
import { computed, ref } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // conversation.custom_attributes, gravado pelo CtwaReferralProcessor.
  attrs: { type: Object, default: () => ({}) },
  // O card do fio já mostra o título no cabeçalho; o painel não tem cabeçalho.
  showHeadline: { type: Boolean, default: false },
});

const { t } = useI18n();

const headline = computed(() => props.attrs?.ctwa_headline || '');
const body = computed(() => props.attrs?.ctwa_body || '');
const sourceUrl = computed(() => props.attrs?.ctwa_source_url || '');

// Imagem do criativo: a original quando existe, senão a miniatura.
const creativeUrl = computed(
  () => props.attrs?.ctwa_media_url || props.attrs?.ctwa_thumbnail_url || ''
);
// Falha silenciosa: se o CDN da Meta recusar, o bloco some. Nunca deixar
// placeholder quebrado numa conversa que o cliente está lendo.
const creativeFailed = ref(false);
const showCreative = computed(
  () => Boolean(creativeUrl.value) && !creativeFailed.value
);

// Campanha: no máximo 3 chips, só o que existir.
const campaignChips = computed(() =>
  [
    props.attrs?.utm_campaign,
    props.attrs?.utm_content,
    props.attrs?.utm_source,
  ].filter(Boolean)
);

// Linhas técnicas: só interessam a quem for investigar.
const extraRows = computed(() =>
  [
    { key: 'SOURCE_ID', value: props.attrs?.ctwa_source_id, copyable: false },
    { key: 'CLICK_ID', value: props.attrs?.ctwa_clid, copyable: true },
    {
      key: 'SOURCE_TYPE',
      value: props.attrs?.ctwa_source_type,
      copyable: false,
    },
  ].filter(row => Boolean(row.value))
);

const showTechnical = ref(false);

const onCopy = async value => {
  await copyTextToClipboard(value);
  useAlert(t('CUSTOM_ATTRIBUTES.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div>
    <img
      v-if="showCreative"
      :src="creativeUrl"
      alt=""
      class="ad-creative mb-2.5 h-[118px] w-full rounded-[10px] object-cover"
      @error="creativeFailed = true"
    />
    <p
      v-if="showHeadline && headline"
      class="m-0 mb-1 text-xs font-bold leading-snug text-n-slate-12"
    >
      {{ headline }}
    </p>
    <p
      v-if="body"
      class="m-0 whitespace-pre-line text-xs leading-snug text-n-slate-11"
    >
      {{ body }}
    </p>
    <div v-if="campaignChips.length" class="mt-2.5 flex flex-wrap gap-1.5">
      <span
        v-for="chip in campaignChips"
        :key="chip"
        class="ad-chip max-w-full truncate rounded-full bg-n-blue-3 px-2.5 py-0.5 text-[11px] font-bold text-n-blue-11"
        :title="chip"
      >
        {{ chip }}
      </span>
    </div>
    <a
      v-if="sourceUrl"
      :href="sourceUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="mt-2.5 inline-flex w-fit items-center gap-1 text-xs font-semibold text-n-blue-9 hover:underline"
    >
      <Icon class="size-3" icon="i-ri-external-link-line" />
      {{ $t('CONVERSATION.AD_ARRIVAL.OPEN_SOURCE') }}
    </a>
    <template v-if="extraRows.length">
      <button
        type="button"
        data-testid="ad-technical-toggle"
        class="mt-3 flex w-full items-center gap-1.5 border-t border-n-weak pt-2 text-xs font-bold text-n-slate-11 transition-colors hover:text-n-slate-12"
        :aria-expanded="showTechnical"
        @click="showTechnical = !showTechnical"
      >
        {{ $t('CONVERSATION.AD_ARRIVAL.TECH_DETAILS') }}
        <Icon
          class="ms-auto size-3.5 transition-transform duration-150"
          :class="{ 'rotate-180': showTechnical }"
          icon="i-ri-arrow-down-s-line"
        />
      </button>
      <div v-if="showTechnical" class="ad-reveal grid gap-1 pt-2">
        <div
          v-for="row in extraRows"
          :key="row.key"
          class="group/adrow flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span class="shrink-0 text-n-slate-11">
            {{ $t(`CONVERSATION.AD_ARRIVAL.${row.key}`) }}
          </span>
          <span
            class="min-w-0 flex-1 truncate text-right text-n-slate-12"
            :title="row.value"
          >
            {{ row.value }}
          </span>
          <button
            v-if="row.copyable"
            type="button"
            class="ad-copy hidden shrink-0 rounded p-0.5 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 group-hover/adrow:block"
            :title="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
            @click="onCopy(row.value)"
          >
            <Icon class="size-3" icon="i-ri-clipboard-line" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* O criativo e as linhas técnicas entram descendo, no mesmo tempo da expansão
   do card — sem isso o bloco pisca inteiro de uma vez. */
@keyframes ad-slide-down {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
}
@keyframes ad-rise {
  from {
    opacity: 0;
    transform: scale(0.985);
  }
}
.ad-creative {
  animation: ad-rise 0.32s cubic-bezier(0.2, 0.7, 0.3, 1) 0.06s both;
}
.ad-chip,
.ad-reveal {
  animation: ad-slide-down 0.22s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}
.ad-chip:nth-child(2) {
  animation-delay: 0.05s;
}
.ad-chip:nth-child(3) {
  animation-delay: 0.1s;
}
/* No toque não existe hover: o copiar do Click ID fica sempre visível. */
@media (hover: none) {
  .ad-copy {
    display: block;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ad-creative,
  .ad-chip,
  .ad-reveal {
    animation: none;
  }
}
</style>
